import { useCallback } from 'react';
import { newDocId } from '../../doc/clipIds';
import { insertClip, replaceClip } from '../../doc/clipOps';
import { useClipLimits } from '../../doc/useClipLimits';
import type { FootageView } from '../../footage/footageView';
import { useMusicVideoEditor } from '../../MusicVideoEditorContext';
import { dropLengthSec, snapTime } from '../../timeline/timelineGeometry';

/**
 * The keyboard and click way of a drag (design b1): `add` puts a clip at the beat of the play head,
 * `replaceSelected` gives the selected clip this footage and keeps its length (design c4).
 */
export function useAddClip() {
  const { change, songMap, transport, select, selection, readOnly } = useMusicVideoEditor();
  const limitsFor = useClipLimits();

  const add = useCallback(
    (view: FootageView) => {
      if (readOnly) {
        return;
      }
      const id = newDocId();
      const atSec = snapTime(transport.livePositionSec(), songMap.beats, songMap.bpm, false);
      change((doc) =>
        insertClip(
          doc,
          { id, source: view.source, atSec, lengthSec: dropLengthSec(songMap.bpm, view.durationSec) },
          () => newDocId(),
          limitsFor(view)
        )
      );
      select({ kind: 'clip', id });
    },
    [change, limitsFor, readOnly, select, songMap.beats, songMap.bpm, transport]
  );

  const selectedClipId = selection?.kind === 'clip' ? selection.id : null;
  const replaceSelected = useCallback(
    (view: FootageView) => {
      if (readOnly || selectedClipId === null) {
        return;
      }
      change((doc) => replaceClip(doc, selectedClipId, view.source, limitsFor(view)));
    },
    [change, limitsFor, readOnly, selectedClipId]
  );

  return { add, replaceSelected, hasSelection: selectedClipId !== null };
}
